import React from 'react'
import { useTranslation } from 'react-i18next'
import { Icon, IconButton } from '../../../atoms'
import { getFileExtension, formatFileSize } from '../utils'
import {
  FileItem,
  FileIcon,
  FileContent,
  FileName,
  FileDetails,
  FileActions,
  DeleteConfirmOverlay,
  DeleteConfirmText,
  DeleteConfirmActions,
  DeleteConfirmButton,
} from './FileCard.styles'
import type { FileCardProps } from './FileCard.types'
import type { TFunction } from 'i18next'

// Format relative time for display with translations
const formatRelativeTime = (
  dateString: string | undefined,
  t: TFunction<'ui', undefined>
) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return t('files.time.justNow')
  if (diffMins < 60) return t('files.time.minutesAgo', { count: diffMins })
  if (diffHours < 24) return t('files.time.hoursAgo', { count: diffHours })
  if (diffDays < 7) return t('files.time.daysAgo', { count: diffDays })
  return date.toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' })
}

export const FileCard: React.FC<FileCardProps> = ({
  file,
  onClick,
  onDownload,
  onDelete,
  isDeleting,
  onCancelDelete,
  onConfirmDelete,
}) => {
  const { t } = useTranslation('ui')

  const handleCardClick = () => {
    if (onClick) {
      onClick(file)
    } else if (onDownload) {
      onDownload(file)
    }
  }

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onDownload) {
      onDownload(file)
    }
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onDelete) {
      onDelete(file)
    }
  }

  const handleConfirmDelete = () => {
    if (onConfirmDelete) {
      onConfirmDelete(file)
    }
  }

  const handleCancelDelete = () => {
    if (onCancelDelete) {
      onCancelDelete()
    }
  }

  return (
    <FileItem
      data-searchable
      data-search-text={`${file.name || ''} ${file.original_name || ''} ${file.description || ''} ${file.uploaded_by_name || ''} ${file.category || ''}`}
      onClick={handleCardClick}
    >
      {isDeleting ? (
        <DeleteConfirmOverlay>
          <DeleteConfirmText>{t('files.confirmDelete')}</DeleteConfirmText>
          <DeleteConfirmActions>
            <DeleteConfirmButton onClick={handleCancelDelete}>
              {t('common.actions.cancel')}
            </DeleteConfirmButton>
            <DeleteConfirmButton
              $variant="danger"
              onClick={handleConfirmDelete}
            >
              {t('common.actions.delete')}
            </DeleteConfirmButton>
          </DeleteConfirmActions>
        </DeleteConfirmOverlay>
      ) : (
        <>
          <FileIcon $type={file.type}>
            <span>{getFileExtension(file.name)}</span>
          </FileIcon>
          <FileContent>
            <FileName>{file.name}</FileName>
            <FileDetails>
              <span>{formatFileSize(file.size)}</span>
              {file.uploaded_by_name && <span>{file.uploaded_by_name}</span>}
              <span>{formatRelativeTime(file.create_timestamp, t)}</span>
            </FileDetails>
          </FileContent>
          <FileActions>
            {onDownload && (
              <IconButton
                size="small"
                variant="default"
                onClick={handleDownload}
                title={t('files.downloadFile')}
                aria-label={t('files.downloadFile')}
              >
                <Icon name="download" size="small" />
              </IconButton>
            )}
            {onDelete && (
              <IconButton
                size="small"
                variant="default"
                onClick={handleDelete}
                title={t('files.deleteFile')}
                aria-label={t('files.deleteFile')}
              >
                <Icon name="delete" size="small" />
              </IconButton>
            )}
          </FileActions>
        </>
      )}
    </FileItem>
  )
}
